import { captureException } from '@sentry/vue';

import { SemboDate } from '@libs/sembo-dates';
import type {
  ExposedIngredient,
  ExposedRecipe,
  Month,
  OldExposedIngredient,
  OldExposedRecipe,
} from '@libs/trip-planner-interfaces';
import { trackReisegigantenDeserializeOldRecipe } from '@libs/google-analytics-events';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';
import {
  exhaustiveGuard,
  isReisegiganten,
  localeStringComparer,
  normalizeDateOnlyString,
} from '@libs/utils';
import type { Serializer } from '@libs/utils';

import type { RecipeMainIngredient } from '../../../recipe/ingredients';
import { isFlightIngredient } from '../../../recipe/ingredients';
import { TripPlannerPax } from '../../../recipe/pax';
import {
  isExposedFerryTransportIngredient,
  isExposedFlightTransportIngredient,
  isExposedHotelIngredient,
  isExposedOwnStayIngredient,
  isExposedOwnTransportIngredient,
  isFerryIngredient,
  isHotelIngredient,
  isOwnStayIngredient,
  isOwnTransportIngredient,
} from '../../../recipe/ingredients/typeGuards';
import type { TripPlannerProperties } from '../../propsRouteBuilder/legacy/model';

import {
  createExposedFerryIngredient,
  createFerryIngredient,
} from './recipeSerializer.ferry.helpers';
import {
  createExposedFlightIngredient,
  createFlightIngredient,
} from './recipeSerializer.flight.helpers';
import {
  encodeRecipe,
  decodeRecipe,
  isOldExposedRecipe,
} from './recipeSerializer.helpers';
import {
  createExposedHotelIngredient,
  createHotelIngredient,
} from './recipeSerializer.hotel.helpers';
import {
  createExposedOwnStayIngredient,
  createOwnStayIngredient,
} from './recipeSerializer.ownStay.helpers';
import {
  createExposedOwnTransportIngredient,
  createOwnTransportIngredient,
} from './recipeSerializer.ownTransport.helpers';

export const RecipeSerializer: Serializer<TripPlannerProperties> = {
  serialize: function (recipe: TripPlannerProperties): string {
    const {
      initialIngredients,
      initialPax,
      initialSelectedDate,
      initialSuggestionSortType,
      metaData,
    } = recipe;
    const ingredientsToEncode = initialIngredients.map((ingredient) => {
      if (isFlightIngredient(ingredient)) {
        return createExposedFlightIngredient(ingredient);
      }

      if (isFerryIngredient(ingredient)) {
        return createExposedFerryIngredient(ingredient);
      }

      if (isHotelIngredient(ingredient)) {
        return createExposedHotelIngredient(ingredient);
      }

      if (isOwnStayIngredient(ingredient)) {
        return createExposedOwnStayIngredient(ingredient);
      }

      if (isOwnTransportIngredient(ingredient)) {
        return createExposedOwnTransportIngredient(ingredient);
      }

      return exhaustiveGuard(ingredient, 'Invalid RecipeMainIngredient');
    });

    const selectedDateToEncode = initialSelectedDate
      ? normalizeDateOnlyString(initialSelectedDate)
      : undefined;

    const recipeToEncode: ExposedRecipe = {
      ingredients: ingredientsToEncode,
      pax: initialPax || [{ numberOfAdults: 2 }],
      selectedDate: selectedDateToEncode,
      suggestedBy: initialSuggestionSortType,
      metadata: metaData,
    };
    return encodeRecipe(recipeToEncode);
  },
  deserialize: function (value: string): TripPlannerProperties {
    const recipeJSON = decodeRecipe(value);

    if (
      recipeJSON.affiliateId &&
      isReisegiganten(recipeJSON.affiliateId) &&
      isOldExposedRecipe(recipeJSON)
    ) {
      trackReisegigantenDeserializeOldRecipe();
    }

    const {
      ingredients: decodedIngredients,
      pax: decodedPax,
      sessionId,
    } = recipeJSON;

    const {
      selectedDate: decodedSelectedDate,
      selectedMonth: decodedSelectedMonth,
      suggestedBy: decodedSuggestionSortType,
      startDateOnly,
      endDateOnly,
      metadata,
      searchForPackageRates,
    } = recipeJSON as ExposedRecipe;

    // Backwards Compatibility
    const { suggestionSortType: oldDecodedSuggestionSortType } =
      recipeJSON as OldExposedRecipe;

    let mappedDecodedIngredients: RecipeMainIngredient[] = [];

    try {
      mappedDecodedIngredients = decodedIngredients.map(
        (ingredient: ExposedIngredient | OldExposedIngredient) => {
          if (isExposedHotelIngredient(ingredient)) {
            return createHotelIngredient(ingredient);
          }

          if (isExposedOwnStayIngredient(ingredient)) {
            return createOwnStayIngredient(ingredient);
          }

          if (isExposedOwnTransportIngredient(ingredient)) {
            return createOwnTransportIngredient(ingredient);
          }

          if (isExposedFlightTransportIngredient(ingredient)) {
            return createFlightIngredient(ingredient);
          }

          if (isExposedFerryTransportIngredient(ingredient)) {
            return createFerryIngredient(ingredient);
          }

          return exhaustiveGuard(ingredient, 'Invalid ExposedIngredient');
        }
      );
    } catch (error) {
      captureException(error);
    }

    // NOTE: Temporarily while we investigate the issue with extended logging, if any ingredient can not be decoded we return no ingredients at all
    const ingredients: RecipeMainIngredient[] =
      mappedDecodedIngredients.length === decodedIngredients.length
        ? mappedDecodedIngredients
        : [];

    // Backwards Compatibility
    const pax = TripPlannerPax.isOldPax(decodedPax)
      ? TripPlannerPax.getPaxFromOldPax(decodedPax)
      : decodedPax;

    const selectedDate = decodedSelectedDate
      ? new SemboDate(decodedSelectedDate).get()
      : undefined;

    const selectedMonth: Month | undefined =
      startDateOnly && endDateOnly
        ? ((new Date(startDateOnly).getMonth() + 1) as Month)
        : decodedSelectedMonth;

    const suggestedSortType =
      decodedSuggestionSortType ?? oldDecodedSuggestionSortType;

    const suggestedBy = !suggestedSortType
      ? undefined
      : localeStringComparer(suggestedSortType, 'Cheapest', {
          sensitivity: 'accent',
        }) === 0
      ? AvailabilityOrder.Cheapest
      : AvailabilityOrder.Popular;

    return {
      initialIngredients: ingredients,
      initialPax: pax,
      initialSelectedDate: selectedDate,
      initialSelectedMonth: selectedMonth,
      initialSuggestionSortType: suggestedBy,
      sessionId,
      metaData: metadata,
      searchForPackageRates,
    };
  },
};
